import React, { useEffect, useState, useRef } from 'react';
import { localToday, dateStr } from '../utils/dates';
import { logError } from '../utils/errorLogger';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection, getDocs, getDoc, doc, addDoc, updateDoc, setDoc, deleteDoc, orderBy, query, where
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import {
  Box, Container, Typography, Card, CardContent, Button, TextField, MenuItem, Alert,
  CircularProgress, Checkbox, FormControlLabel, Switch, Chip, Grid,
  Collapse, Divider, IconButton, Tooltip, Tabs, Tab, InputAdornment,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import WavesIcon from '@mui/icons-material/Waves';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useBranding } from '../contexts/BrandingContext';

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function loadQuill() {
  return new Promise(resolve => {
    if (window.Quill) { resolve(window.Quill); return; }
    if (!document.getElementById('quill-css')) {
      const link = document.createElement('link');
      link.id = 'quill-css'; link.rel = 'stylesheet';
      link.href = 'https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.min.js';
    script.onload = () => resolve(window.Quill);
    document.head.appendChild(script);
  });
}

const GROUP_ORDER = ['Adult', 'Junior', 'Camps & Clinics'];
const GROUP_COLORS = { Adult: 'primary', Junior: 'success', 'Camps & Clinics': 'info' };

// Wave defaults by index
const WAVE_DEFAULTS = [
  { name: 'Wave 1', day: 1, start_time: '08:30', end_time: '15:30' },
  { name: 'Wave 2', day: 1, start_time: '16:00', end_time: '21:30' },
  { name: 'Wave 3', day: 2, start_time: '08:30', end_time: '15:30' },
];

// ── Time helpers ────────────────────────────────────────────────────────────
function addMinutes(timeStr, mins) {
  const [h, m] = timeStr.split(':').map(Number);
  const total = h * 60 + m + mins;
  const newH = Math.max(0, Math.min(23, Math.floor(total / 60)));
  const newM = ((total % 60) + 60) % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}

// Create/update reservations for a tournament based on its waves and date
async function syncTournamentReservations(db, tournamentId, tournamentName, tournamentDate, waves, courtsMap = {}, eventTimes = {}) {
  // Delete existing tournament reservations for this tournament
  const existing = await getDocs(
    query(collection(db, 'reservations'),
      where('tournamentId', '==', tournamentId),
      where('type', '==', 'tournament'))
  );
  await Promise.all(existing.docs.map(d => deleteDoc(d.ref)));

  if (!waves || waves.length === 0 || !tournamentDate) return;

  // Group waves by day
  const byDay = {};
  waves.forEach(w => {
    const day = w.day || 1;
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(w);
  });

  // For each day, compute the date and time range
  const baseDateParts = tournamentDate.split('-').map(Number);
  const baseDate = new Date(baseDateParts[0], baseDateParts[1] - 1, baseDateParts[2]);

  for (const [day, dayWaves] of Object.entries(byDay)) {
    const dayOffset = parseInt(day) - 1;
    const d = new Date(baseDate);
    d.setDate(d.getDate() + dayOffset);
    const dStr = dateStr(d);

    // The tournament's event start/end times define the booking window when
    // set; otherwise fall back to the earliest/latest wave times for the day.
    const starts = dayWaves.map(w => w.start_time).filter(Boolean).sort();
    const ends = dayWaves.map(w => w.end_time).filter(Boolean).sort();
    const earliest = eventTimes.start || starts[0];
    const latest = eventTimes.end || ends[ends.length - 1];
    if (!earliest || !latest) continue;

    // Courts are held 30 minutes before the event starts through 30 minutes
    // after it ends (setup and teardown).
    const resStart = addMinutes(earliest, -30);
    const resEnd = addMinutes(latest, 30);

    // Derive courts from division assignments for this day's waves
    const dayDivisions = dayWaves.flatMap(w => w.divisions || []);
    const assignedCourts = [...new Set(
      dayDivisions
        .map(divName => courtsMap[divName])
        .filter(Boolean)
        .map(n => `court-${n}`)
    )].sort();
    const courts = assignedCourts.length > 0
      ? assignedCourts
      : ['court-1','court-2','court-3','court-4','court-5','court-6'];

    await addDoc(collection(db, 'reservations'), {
      title: tournamentName,
      type: 'tournament',
      tournamentId,
      day: parseInt(day),
      // dStr, not dateStr — the latter is the imported helper, and passing the
      // function itself is what Firestore rejected as an unsupported value.
      date: dStr,
      startTime: resStart,
      endTime: resEnd,
      courts,
      notes: `Auto-created from tournament waves (Day ${day})`,
      createdAt: new Date().toISOString(),
    });
  }
}

function nowDate() { return localToday(); }
function nowTime() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
}
// The parameter used to be called dateStr, which shadowed the imported helper
// of the same name — so the last line invoked a string and threw. Renamed
// rather than aliased, because the shadowing is the bug.
function dayBefore(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() - 1);
  return dateStr(d);
}

export default function TournamentFormPage() {
  const branding = useBranding();
  const { tid } = useParams();
  const isEdit = Boolean(tid);
  // Set by the Duplicate button on the tournament list.
  const [searchParams] = useSearchParams();
  const copyParam = isEdit ? '' : (searchParams.get('copy') || '');
  const navigate = useNavigate();

  const [divisions, setDivisions] = useState([]);
  const [copySources, setCopySources] = useState([]); // existing tournaments, add mode only
  const [copyFromId, setCopyFromId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  // ── Staff tab: roster of workers assigned to this event + their logged work ──
  const [staffPeople, setStaffPeople] = useState([]);
  const [staffRoster, setStaffRoster] = useState([]);
  const [staffPickId, setStaffPickId] = useState('');
  const [staffEntries, setStaffEntries] = useState([]);
  const [staffHoursForm, setStaffHoursForm] = useState({});
  const [staffSavingId, setStaffSavingId] = useState('');

  const [name, setName] = useState('');
  // Distinguishes a regular tournament from an open event (clinic, social, etc.).
  // Stored as 'tournament' | 'event' on the tournament doc; defaults to
  // 'tournament' for new records and any pre-existing docs without the field.
  const [eventType, setEventType] = useState('tournament');
  // CashCow pays per match won. Rates live on the tournament rather than in
  // settings because they are the lever the format is tuned with — shifting
  // money between pool play and the final changes what the event feels like.
  const [payouts, setPayouts] = useState({ pool: 10, qf: 25, sf: 50, final: 150,
    undefeatedPool: 0, undefeatedOverall: 0 });
  const [slug, setSlug] = useState('');
  const [date, setDate] = useState('');
  const [location, setLocation] = useState(branding.facilityName);
  const [directorEmail, setDirectorEmail] = useState('');
  const [selectedDivs, setSelectedDivs] = useState({});
  const [caps, setCaps] = useState({});
  const [priceOverrides, setPriceOverrides] = useState({});
  const [freeIfRegistered, setFreeIfRegistered] = useState({});
  const [divCourts, setDivCourts] = useState({});
  const [published, setPublished] = useState(false);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [publishDate, setPublishDate] = useState(nowDate());
  const [publishTime, setPublishTime] = useState(nowTime());
  const [closeDate, setCloseDate] = useState('');
  const [closeTime, setCloseTime] = useState('18:00');
  const [refundDate, setRefundDate] = useState('');
  const [refundTime, setRefundTime] = useState('18:00');
  const [lateDate, setLateDate] = useState('');
  const [lateTime, setLateTime] = useState('18:00');
  // Optional event-day times. Display-only metadata so admins / players know
  // when the tournament actually runs (separate from registration windows).
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [eventArrivalTime, setEventArrivalTime] = useState('');
  const [lateFeeOverrides, setLateFeeOverrides] = useState({}); // per-division override; blank = inherit default
  const [groupDefaults, setGroupDefaults] = useState({ late_fees: {}, entry_fees: {}, caps: {} });
  const [sanctioningBody, setSanctioningBody] = useState('None');

  // Waves — per tournament only
  const [waves, setWaves] = useState([{ ...WAVE_DEFAULTS[0], divisions: [] }]);
  const [waveOpen, setWaveOpen] = useState(false);

  const quillInst = useRef(null);

  useEffect(() => { loadData(); }, [tid]);

  // Compute currently selected division names
  const selectedDivNames = divisions.filter(d => selectedDivs[d.name]).map(d => d.name);

  // When selectedDivs changes, sync Wave 1 to include any newly selected divs
  // that aren't already assigned to a wave
  useEffect(() => {
    if (selectedDivNames.length === 0) return;
    setWaves(prev => {
      const allAssigned = new Set(prev.flatMap(w => w.divisions));
      const unassigned = selectedDivNames.filter(d => !allAssigned.has(d));
      if (unassigned.length === 0) return prev;
      // Add unassigned to wave 1
      return prev.map((w, i) => i === 0 ? { ...w, divisions: [...w.divisions, ...unassigned] } : w);
    });
  }, [JSON.stringify(selectedDivNames)]);

  async function loadData() {
    try {
      const divSnap = await getDocs(query(collection(db, 'divisions'), orderBy('sort_order')));
      const divList = divSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setDivisions(divList);

      const bSnap = await getDoc(doc(db, 'settings', 'branding'));
      const b = bSnap.exists() ? bSnap.data() : {};

      const defSnap = await getDoc(doc(db, 'settings', 'defaultDivisions'));
      const defDivs = defSnap.exists() ? JSON.parse(defSnap.data().value || '[]') : [];

      const infoSnap = await getDoc(doc(db, 'settings', 'tournamentInfo'));
      const defInfo = infoSnap.exists() ? (infoSnap.data().html || '') : '';

      // Group-level defaults: { late_fees: {Adult: 20}, entry_fees: {Adult: 80}, caps: {Adult: 8} }
      const groupsSnap = await getDoc(doc(db, 'settings', 'division_groups'));
      const groupsData = groupsSnap.exists() ? groupsSnap.data() : {};
      setGroupDefaults({
        late_fees:  groupsData.late_fees  || {},
        entry_fees: groupsData.entry_fees || {},
        caps:       groupsData.caps       || {},
      });

      try {
        const uSnap = await getDocs(collection(db, 'users'));
        const staffDocs = uSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(u => (u.roles || []).some(r => ['contractor', 'coach', 'assistant_coach'].includes(r)));
        // Dedupe by email, preferring a REAL uid doc over a pending_/mini_ ghost,
        // so the roster never captures a stale id that work entries can't match.
        const isGhost = (id) => id.startsWith('pending_') || id.startsWith('mini_');
        const byEmail = new Map();
        for (const u of staffDocs) {
          const key = (u.email || '').trim().toLowerCase() || u.id;
          const existing = byEmail.get(key);
          if (!existing) { byEmail.set(key, u); continue; }
          if (isGhost(existing.id) && !isGhost(u.id)) byEmail.set(key, u);
        }
        const staff = [...byEmail.values()]
          .filter(u => !isGhost(u.id) || !staffDocs.some(o => (o.email||'').toLowerCase() === (u.email||'').toLowerCase() && !isGhost(o.id)))
          .map(u => ({ userId: u.id, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id, hourlyRate: u.hourlyRate ?? null }));
        setStaffPeople(staff);
      } catch (err) { console.warn('staff load failed:', err); }

      if (isEdit) {
        const tSnap = await getDoc(doc(db, 'tournaments', tid));
        if (tSnap.exists()) {
          const t = tSnap.data();
          setStaffRoster(Array.isArray(t.staffRoster) ? t.staffRoster : []);
          try {
            const weSnap = await getDocs(query(collection(db, 'workEntries'), where('tournamentId', '==', tid)));
            setStaffEntries(weSnap.docs.map(d => ({ id: d.id, ...d.data() })));
          } catch (err) { console.warn('tournament work entries load failed:', err); }
          setName(t.name || '');
          setEventType(t.eventType || 'tournament');
          setPayouts({
            pool:  Number(t.cashCowPayouts?.pool)  || 10,
            qf:    Number(t.cashCowPayouts?.qf)    || 25,
            sf:    Number(t.cashCowPayouts?.sf)    || 50,
            final: Number(t.cashCowPayouts?.final) || 150,
            // Bonuses default to 0, so an event created before they existed keeps
            // paying exactly what it paid before.
            undefeatedPool:    Number(t.cashCowPayouts?.undefeatedPool)    || 0,
            undefeatedOverall: Number(t.cashCowPayouts?.undefeatedOverall) || 0,
          });
          setSlug(t.slug || (t.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
          setDate(t.date || '');
          setLocation(t.location || '');
          setDirectorEmail(t.director_email || b.director_email || '');
          setPublished(t.active || false);
          setPublishDate(t.publish_date || nowDate());
          setPublishTime(t.publish_time || nowTime());
          setCloseDate(t.close_date || '');
          setCloseTime(t.close_time || '18:00');
          setRefundDate(t.refund_date || '');
          setRefundTime(t.refund_time || '18:00');
          setLateDate(t.late_date || '');
          setLateTime(t.late_time || '18:00');
          setEventStartTime(t.eventStartTime || '');
          setEventEndTime(t.eventEndTime || '');
          setEventArrivalTime(t.eventArrivalTime || '');
          setSanctioningBody(t.sanctioning_body || 'None');
          const tDivs = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
          const tCaps = typeof t.caps === 'string' ? JSON.parse(t.caps) : (t.caps || {});
          const tPrices = typeof t.prices === 'string' ? JSON.parse(t.prices) : (t.prices || {});
          const tLateFees = typeof t.late_fees === 'string' ? JSON.parse(t.late_fees) : (t.late_fees || {});
          const tFree = t.free_if_registered || {};
          const tCourts = t.courts || {};
          const selMap = {}; const capMap = {}; const priceMap = {}; const lateFeeMap = {}; const freeMap = {}; const courtsInitMap = {};
          divList.forEach(d => {
            selMap[d.name] = tDivs.includes(d.name);
            capMap[d.name] = tCaps[d.name] != null ? String(tCaps[d.name]) : (d.cap ? String(d.cap) : '');
            priceMap[d.name] = tPrices[d.name] != null ? String(tPrices[d.name]) : '';
            lateFeeMap[d.name] = tLateFees[d.name] != null ? String(tLateFees[d.name]) : '';
            freeMap[d.name] = tFree[d.name] || false;
            courtsInitMap[d.name] = tCourts[d.name] != null ? String(tCourts[d.name]) : '';
          });
          setSelectedDivs(selMap); setCaps(capMap); setPriceOverrides(priceMap); setLateFeeOverrides(lateFeeMap); setFreeIfRegistered(freeMap); setDivCourts(courtsInitMap);

          // Load existing waves or default to Wave 1 with all divisions
          if (t.waves && t.waves.length > 0) {
            setWaves(t.waves);
          } else {
            setWaves([{ ...WAVE_DEFAULTS[0], divisions: tDivs }]);
          }
          setTimeout(() => initQuill(t.info_html || defInfo), 100);
        }
      } else {
        try {
          const listSnap = await getDocs(collection(db, 'tournaments'));
          setCopySources(listSnap.docs
            .map(d => ({ id: d.id, name: d.data().name || '(unnamed)', date: d.data().date || '' }))
            .sort((a, b2) => (b2.date || '').localeCompare(a.date || '')));
        } catch (e) { console.warn('copy-source list load failed:', e); }
        setDirectorEmail(b.director_email || '');
        const selMap = {}; const capMap = {}; const freeMap = {};
        divList.forEach(d => {
          selMap[d.name] = defDivs.includes(d.name);
          capMap[d.name] = d.cap ? String(d.cap) : '';
          freeMap[d.name] = false;
        });
        setSelectedDivs(selMap); setCaps(capMap); setFreeIfRegistered(freeMap);
        // Default: Wave 1 with all default divisions
        setWaves([{ ...WAVE_DEFAULTS[0], divisions: defDivs }]);
        setTimeout(() => initQuill(defInfo), 100);
      }
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }

  // Copy an existing tournament into the form (add mode). Mirrors the edit
  // hydration, with copy resets: fresh name/slug, no date, unpublished, and
  // all date-specific deadlines cleared (their times are kept as defaults).
  const handleCopyFrom = async (sourceId) => {
    setCopyFromId(sourceId);
    if (!sourceId) return;
    try {
      const tSnap = await getDoc(doc(db, 'tournaments', sourceId));
      if (!tSnap.exists()) return;
      const t = tSnap.data();
      const copyName = `${t.name || ''} (Copy)`;
      setName(copyName);
      setEventType(t.eventType || 'tournament');
      // Copying a CashCow event carries its rates: the whole point of copying
      // is to run the same event again.
      setPayouts({
        pool:  Number(t.cashCowPayouts?.pool)  || 10,
        qf:    Number(t.cashCowPayouts?.qf)    || 25,
        sf:    Number(t.cashCowPayouts?.sf)    || 50,
        final: Number(t.cashCowPayouts?.final) || 150,
        // Bonuses default to 0, so an event created before they existed keeps
        // paying exactly what it paid before.
        undefeatedPool:    Number(t.cashCowPayouts?.undefeatedPool)    || 0,
        undefeatedOverall: Number(t.cashCowPayouts?.undefeatedOverall) || 0,
      });
      setSlug(copyName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
      setDate('');
      setLocation(t.location || '');
      setDirectorEmail(t.director_email || directorEmail);
      setPublished(false);
      setPublishDate(nowDate());
      setPublishTime(nowTime());
      setCloseDate('');
      setCloseTime(t.close_time || '18:00');
      setRefundDate('');
      setRefundTime(t.refund_time || '18:00');
      setLateDate('');
      setLateTime(t.late_time || '18:00');
      setEventStartTime(t.eventStartTime || '');
      setEventEndTime(t.eventEndTime || '');
      setEventArrivalTime(t.eventArrivalTime || '');
      setSanctioningBody(t.sanctioning_body || 'None');
      const tDivs = Array.isArray(t.divisions) ? t.divisions : JSON.parse(t.divisions || '[]');
      const tCaps = typeof t.caps === 'string' ? JSON.parse(t.caps) : (t.caps || {});
      const tPrices = typeof t.prices === 'string' ? JSON.parse(t.prices) : (t.prices || {});
      const tLateFees = typeof t.late_fees === 'string' ? JSON.parse(t.late_fees) : (t.late_fees || {});
      const tFree = t.free_if_registered || {};
      const tCourts = t.courts || {};
      const selMap = {}; const capMap = {}; const priceMap = {}; const lateFeeMap = {}; const freeMap = {}; const courtsInitMap = {};
      divisions.forEach(d => {
        selMap[d.name] = tDivs.includes(d.name);
        capMap[d.name] = tCaps[d.name] != null ? String(tCaps[d.name]) : (d.cap ? String(d.cap) : '');
        priceMap[d.name] = tPrices[d.name] != null ? String(tPrices[d.name]) : '';
        lateFeeMap[d.name] = tLateFees[d.name] != null ? String(tLateFees[d.name]) : '';
        freeMap[d.name] = tFree[d.name] || false;
        courtsInitMap[d.name] = tCourts[d.name] != null ? String(tCourts[d.name]) : '';
      });
      setSelectedDivs(selMap); setCaps(capMap); setPriceOverrides(priceMap); setLateFeeOverrides(lateFeeMap); setFreeIfRegistered(freeMap); setDivCourts(courtsInitMap);
      if (t.waves && t.waves.length > 0) setWaves(t.waves);
      else setWaves([{ ...WAVE_DEFAULTS[0], divisions: tDivs }]);
      setTimeout(() => initQuill(t.info_html || ''), 100);
    } catch (e) { console.error('copy failed:', e); }
  };

  // The Duplicate button lands here with ?copy=<id>: the same copy as picking
  // it from the list, run once the divisions it maps onto have loaded.
  useEffect(() => {
    if (copyParam && !loading && divisions.length && !copyFromId) handleCopyFrom(copyParam);
  }, [copyParam, loading, divisions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-init Quill when Tab 4 (Info Page) becomes active. The tab's DOM
  // unmounts when inactive, so every entry needs a rebuild — and every exit
  // must capture what was typed before the instance is orphaned.
  useEffect(() => {
    if (activeTab !== 4) return;
    const timer = setTimeout(() => {
      initQuill(quillInst._pendingHtml || '');
    }, 100);
    return () => {
      clearTimeout(timer);
      if (quillInst.current) {
        const html = quillInst.current.root.innerHTML;
        if (html && html !== '<p><br></p>') quillInst._pendingHtml = html;
        quillInst.current = null;
      }
    };
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  function initQuill(html) {
    quillInst._pendingHtml = html; // always remember the latest desired content
    // The Info tab unmounts its DOM when inactive — an instance whose root is
    // no longer in the document is orphaned and must be rebuilt.
    if (quillInst.current && !document.body.contains(quillInst.current.root)) {
      quillInst.current = null;
    }
    if (quillInst.current) {
      // Live editor: replace content in place (used by copy-from-existing)
      quillInst.current.setContents([]);
      if (html) quillInst.current.clipboard.dangerouslyPasteHTML(html);
      return;
    }
    loadQuill().then(Quill => {
      if (quillInst.current) return;
      if (!document.getElementById('quill-tournament')) return; // div not mounted yet
      quillInst.current = new Quill('#quill-tournament', {
        theme: 'snow',
        placeholder: 'Enter tournament details...',
        modules: { toolbar: [[{ header:[1,2,3,false] }],['bold','italic','underline'],[{ color:[] },{ background:[] }],[{ align:[] }],['link'],[{ list:'ordered' },{ list:'bullet' }],['clean']] }
      });
      if (html) quillInst.current.clipboard.dangerouslyPasteHTML(html);
    });
  }

  function toggleDiv(divName) {
    setSelectedDivs(s => ({ ...s, [divName]: !s[divName] }));
    // If deselecting, remove from all waves
    if (selectedDivs[divName]) {
      setWaves(w => w.map(wave => ({ ...wave, divisions: wave.divisions.filter(d => d !== divName) })));
    }
  }

  // ── Wave functions ──────────────────────────────────────────────────────────
  function addWave() {
  const idx = waves.length;
  const def = WAVE_DEFAULTS[Math.min(idx, WAVE_DEFAULTS.length - 1)];
    setWaves(w => [...w, { ...def, divisions: [] }]);
  }

  function removeWave(idx) {
    if (idx === 0) { alert("Can't remove Wave 1."); return; }
    if (!window.confirm(`Remove Wave ${idx + 1}? Its divisions will move back to Wave 1.`)) return;
    const divToReturn = waves[idx].divisions || [];
    setWaves(w => {
      const updated = w.filter((_, i) => i !== idx);
      // Move orphaned divisions back to Wave 1
      if (divToReturn.length > 0) {
        updated[0] = { ...updated[0], divisions: [...updated[0].divisions, ...divToReturn] };
      }
      return updated;
    });
  }

  function updateWave(idx, field, value) {
    setWaves(w => w.map((wave, i) => i === idx ? { ...wave, [field]: value } : wave));
  }

  // Move a division from its current wave to waveIdx
  function moveDivToWave(waveIdx, divName) {
    setWaves(w => w.map((wave, i) => {
      const divs = (wave.divisions || []).filter(d => d !== divName);
      if (i === waveIdx) return { ...wave, divisions: [...divs, divName] };
      return { ...wave, divisions: divs };
    }));
  }

  function removeDivFromWave(waveIdx, divName) {
    // Move back to wave 1
    setWaves(w => w.map((wave, i) => {
      if (i === waveIdx) return { ...wave, divisions: wave.divisions.filter(d => d !== divName) };
      if (i === 0) return { ...wave, divisions: [...wave.divisions, divName] };
      return wave;
    }));
  }

  const addStaffToRoster = () => {
    if (!staffPickId) return;
    if (staffRoster.some(r => r.userId === staffPickId)) { setStaffPickId(''); return; }
    const p = staffPeople.find(sp => sp.userId === staffPickId);
    if (!p) return;
    setStaffRoster(prev => [...prev, { userId: p.userId, name: p.name }]);
    setStaffPickId('');
  };
  const removeStaffFromRoster = (userId) => {
    setStaffRoster(prev => prev.filter(r => r.userId !== userId));
  };
  const logStaffHours = async (rosterEntry) => {
    if (!isEdit) { alert('Save the tournament first, then log hours.'); return; }
    const form = staffHoursForm[rosterEntry.userId] || {};
    const hours = parseFloat(form.hours);
    const person = staffPeople.find(sp => sp.userId === rosterEntry.userId);
    const rate = parseFloat(form.rate != null && form.rate !== '' ? form.rate : (person?.hourlyRate ?? ''));
    if (isNaN(hours) || hours <= 0) { alert('Enter valid hours.'); return; }
    if (isNaN(rate) || rate < 0) { alert('Enter a valid rate (no default on file).'); return; }
    setStaffSavingId(rosterEntry.userId);
    try {
      const amount = Math.round(hours * rate * 100) / 100;
      const entry = {
        workerId: rosterEntry.userId,
        workerName: rosterEntry.name,
        type: 'hours', hours, rate, amount,
        description: `${name || 'Tournament'} staff`,
        date: date || localToday(),
        tournamentId: tid,
        paid: false, paidAt: null,
        createdBy: auth.currentUser ? auth.currentUser.uid : null,
        createdAt: new Date().toISOString(),
      };
      const ref = await addDoc(collection(db, 'workEntries'), entry);
      setStaffEntries(prev => [...prev, { id: ref.id, ...entry }]);
      setStaffHoursForm(prev => ({ ...prev, [rosterEntry.userId]: { hours: '', rate: '' } }));
    } catch (err) { alert('Log hours failed: ' + err.message); }
    setStaffSavingId('');
  };

  async function handleSubmit(e) {
    e.preventDefault();
    const chosenDivs = divisions.filter(d => selectedDivs[d.name]).map(d => d.name);
    if (chosenDivs.length === 0) { alert('Select at least one division.'); return; }
    setSaving(true);
    try {
      let infoHtml = quillInst.current?.root.innerHTML || quillInst._pendingHtml || '';
      if (infoHtml === '<p><br></p>') infoHtml = '';
      const prices = {}; const capsMap = {}; const lateFees = {}; const freeMap = {}; const courtsMap = {};
      divisions.forEach(d => {
        if (selectedDivs[d.name]) {
          // Entry fee chain: tournament override → division price → group default → 0
          if (priceOverrides[d.name] !== '' && priceOverrides[d.name] != null && priceOverrides[d.name] !== false) {
            prices[d.name] = Number(priceOverrides[d.name]);
          } else if (d.price != null && Number(d.price) > 0) {
            prices[d.name] = Number(d.price);
          } else if (groupDefaults.entry_fees[d.group_name] != null) {
            prices[d.name] = Number(groupDefaults.entry_fees[d.group_name]);
          } else {
            prices[d.name] = 0;
          }
          // Cap chain: tournament override → division cap → group default → null (no cap)
          if (caps[d.name] !== '' && caps[d.name] != null) {
            capsMap[d.name] = Number(caps[d.name]);
          } else if (d.cap != null) {
            capsMap[d.name] = Number(d.cap);
          } else if (groupDefaults.caps[d.group_name] != null) {
            capsMap[d.name] = Number(groupDefaults.caps[d.group_name]);
          } else {
            capsMap[d.name] = null;
          }
          // Late fee chain: tournament override → division default → group default → 0
          if (lateFeeOverrides[d.name] !== '' && lateFeeOverrides[d.name] != null) {
            lateFees[d.name] = Number(lateFeeOverrides[d.name]);
          } else if (d.default_late_fee != null) {
            lateFees[d.name] = Number(d.default_late_fee);
          } else if (groupDefaults.late_fees[d.group_name] != null) {
            lateFees[d.name] = Number(groupDefaults.late_fees[d.group_name]);
          } else {
            lateFees[d.name] = 0;
          }
          if (divCourts[d.name]) courtsMap[d.name] = Number(divCourts[d.name]);
          if (d.group_name === 'Camps & Clinics') {
            freeMap[d.name] = freeIfRegistered[d.name] || false;
          }
        }
      });

      // Clean waves — only keep selected divisions
      const cleanWaves = waves.map(w => ({
        ...w,
        divisions: (w.divisions || []).filter(d => chosenDivs.includes(d)),
      })).filter(w => w.divisions.length > 0 || waves.indexOf(w) === 0);
      // Always keep Wave 1 even if empty
      if (cleanWaves.length === 0) cleanWaves.push({ ...WAVE_DEFAULTS[0], divisions: chosenDivs });

      const payload = {
        name, date, location, slug: slug || name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
        director_email: directorEmail,
        divisions: chosenDivs,
        prices, caps: capsMap, courts: courtsMap, late_fees: lateFees,
        free_if_registered: freeMap,
        waves: cleanWaves,
        active: (() => {
          const publishDt = publishDate ? new Date(`${publishDate}T${publishTime || '00:00'}`) : null;
          return publishDt ? new Date() >= publishDt : published;
        })(),
        info_html: infoHtml,
        publish_date: publishDate || null,
        publish_time: publishTime || null,
        close_date: closeDate || null,
        close_time: closeTime || '18:00',
        refund_date: refundDate || null,
        refund_time: refundTime || '18:00',
        late_date: lateDate || null,
        late_time: lateTime || '18:00',
        eventStartTime: eventStartTime || null,
        eventEndTime: eventEndTime || null,
        eventArrivalTime: eventArrivalTime || null,
        sanctioning_body: sanctioningBody,
        eventType: eventType || 'tournament',
        // Stored for every event, but only read when eventType is 'cashcow' —
        // switching a tournament to the format and back must not lose the rates
        // someone already tuned.
        cashCowPayouts: {
          pool:  Number(payouts.pool)  || 0,
          qf:    Number(payouts.qf)    || 0,
          sf:    Number(payouts.sf)    || 0,
          final: Number(payouts.final) || 0,
          undefeatedPool:    Number(payouts.undefeatedPool)    || 0,
          undefeatedOverall: Number(payouts.undefeatedOverall) || 0,
        },
        staffRoster,
      };
      let savedTid = tid;
      if (isEdit) {
        await updateDoc(doc(db, 'tournaments', tid), payload);
      } else {
        const ref = await addDoc(collection(db, 'tournaments'), { ...payload, created_at: new Date().toISOString() });
        savedTid = ref.id;
      }

      // Sync court reservations based on waves
      try {
        await syncTournamentReservations(db, savedTid, name, date, cleanWaves, courtsMap, { start: eventStartTime, end: eventEndTime });
      } catch (resErr) {
        console.error('Reservation sync failed:', resErr);
        logError('TournamentForm', 'Reservation sync failed: ' + resErr.message, resErr.stack || '');
        alert('Reservation sync failed: ' + resErr.message);
      }
      if (saveAsDefault) {
        await setDoc(doc(db, 'settings', 'defaultDivisions'), { value: JSON.stringify(chosenDivs) });
      }
      navigate('/tournament-admin');
    } catch(e) { logError('TournamentForm', 'Save failed: ' + e.message, e.stack || ''); alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  }

  // Divisions assigned to each wave (for display in the picker)
  function getWaveForDiv(divName) {
    return waves.find(w => (w.divisions || []).includes(divName));
  }

  const grouped = {};
  divisions.forEach(d => {
    const g = d.group_name || 'Adult';
    if (!grouped[g]) grouped[g] = [];
    grouped[g].push(d);
  });
  const groupedEntries = [
    ...GROUP_ORDER.filter(g => grouped[g]).map(g => [g, grouped[g]]),
    ...Object.entries(grouped).filter(([g]) => !GROUP_ORDER.includes(g)),
  ];

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ bgcolor: '#1a2940', color: '#fff', py: 4, mb: 4 }}>
        <Container>
          <Typography variant="h4" fontWeight="bold">
            {isEdit ? 'Edit Tournament' : 'Add Tournament'}
          </Typography>
        </Container>
      </Box>
      <Container maxWidth="md" sx={{ pb: 6 }}>
        {!isEdit && copySources.length > 0 && (
          <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1.5 }}>Start from</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant={copyFromId ? 'outlined' : 'contained'}
                  onClick={() => { if (copyFromId) window.location.reload(); }}>
                  Blank tournament
                </Button>
                <TextField select size="small" label="Copy an existing event" value={copyFromId}
                  onChange={e => handleCopyFrom(e.target.value)} sx={{ minWidth: 280 }}>
                  {copySources.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name}{t.date ? ` — ${t.date}` : ''}</MenuItem>
                  ))}
                </TextField>
              </Box>
              {copyFromId && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#888' }}>
                  Form pre-filled from the selected event. Date, deadlines, and publish status were reset; registrations and results are never copied. Adjust anything below, then save as a new tournament.
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
              sx={{ borderBottom: '1px solid #eee', px: 2 }}
              variant="scrollable" scrollButtons="auto">
              <Tab label="Name & Options" />
              <Tab label="Dates & Times" />
              <Tab label="Divisions" />
              <Tab label="Wave Schedule" />
              <Tab label="Info Page" />
              <Tab label="Staff" />
            </Tabs>
            <form onSubmit={handleSubmit}>
            <Box sx={{ p: 4 }}>

              {/* ── TAB 0: Name & Options ── */}
              {activeTab === 0 ? <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField fullWidth label="Tournament Name" value={name}
                             onChange={e => setName(e.target.value)} required
                             placeholder="e.g. Summer Beach Classic 2026" />
                </Grid>
                {/* Event type lives right of the name so admins set it before
                    anything else. Currently affects the public registration
                    page only (terminology, marketing copy); same data model
                    underneath either way. */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField fullWidth select label="Type" value={eventType}
                             onChange={e => setEventType(e.target.value)} required>
                    <MenuItem value="tournament">Tournament</MenuItem>
                    <MenuItem value="clinic">Clinic</MenuItem>
                    <MenuItem value="camp">Camp</MenuItem>
                    <MenuItem value="tryout">Tryout</MenuItem>
                    <MenuItem value="event">Event</MenuItem>
                    <MenuItem value="cashcow">CashCow (pays per win)</MenuItem>
                  </TextField>
                </Grid>
                {eventType === 'cashcow' && (
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(201,168,76,0.08)',
                      border: '1px solid rgba(201,168,76,0.35)' }}>
                      <Typography sx={{ fontWeight: 700, color: '#1E3A5F', mb: 0.5 }}>
                        Payout per win
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
                        Every match won pays. Paid in cash on the day and tracked on the
                        Financials tab, where you enter each team's wins and tick the
                        bonuses. Leave a bonus at 0 to not offer it.
                      </Typography>
                      <Grid container spacing={2}>
                        {[['pool','Pool play'],['qf','Quarter-final'],['sf','Semi-final'],['final','Final'],
                          ['undefeatedPool','Bonus: undefeated in pool'],
                          ['undefeatedOverall','Bonus: undefeated overall']]
                          .map(([k,label]) => (
                          <Grid size={{ xs: 6, sm: k.startsWith('undefeated') ? 6 : 3 }} key={k}>
                            <TextField fullWidth size="small" type="number" label={label}
                              value={payouts[k]}
                              onChange={e => setPayouts(pv => ({ ...pv, [k]: e.target.value }))}
                              inputProps={{ min: 0, step: 1 }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Grid>
                )}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Date" type="date" value={date}
                             onChange={e => {
                               const d = e.target.value;
                               setDate(d);
                               if (!closeDate || closeDate === dayBefore(date)) setCloseDate(dayBefore(d));
                               if (!refundDate || refundDate === dayBefore(date)) setRefundDate(dayBefore(d));
                             }} required
                             InputLabelProps={{ shrink: true }} />
                </Grid>

                {/* Sanctioning Body */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth select label="Sanctioning Body" value={sanctioningBody}
                    onChange={e => setSanctioningBody(e.target.value)} size="small">
                    {['None','AVP','USAV','p1440','BVNE'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </TextField>
                </Grid>
                {sanctioningBody !== 'None' && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Alert severity="info" sx={{ py: 0.5, fontSize: '0.78rem' }}>
                      Requires {sanctioningBody} membership number at registration.
                    </Alert>
                  </Grid>
                )}

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="URL Slug" value={slug}
                             onChange={e => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/--+/g, '-'))}
                             helperText={`Share link: ${window?.location?.origin || 'https://app.torchvolleyball.com'}/t/${slug || 'your-slug'}`}
                             placeholder="e.g. torch-open-2026" />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="Location" value={location}
                             onChange={e => setLocation(e.target.value)} placeholder={branding.facilityName} />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField fullWidth label="Tournament Director Email" type="email"
                             value={directorEmail} onChange={e => setDirectorEmail(e.target.value)}
                             helperText="Receives notifications for every registration change." />
                </Grid>
              </Grid> : null}

              {/* ── TAB 1: Dates & Times ── */}
              {activeTab === 1 ? <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#1E3A5F', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Visibility & Registration
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Publish Date" type="date" value={publishDate}
                    onChange={e => setPublishDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small"
                    helperText="Controls when publicly visible" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Publish Time" type="time" value={publishTime}
                    onChange={e => setPublishTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Reg. Close Date" type="date" value={closeDate}
                    onChange={e => setCloseDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Reg. Close Time" type="time" value={closeTime}
                    onChange={e => setCloseTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Refund Cutoff Date" type="date" value={refundDate}
                    onChange={e => setRefundDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Refund Cutoff Time" type="time" value={refundTime}
                    onChange={e => setRefundTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Late Registration Starts" type="date" value={lateDate}
                    onChange={e => setLateDate(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small"
                    helperText="Late fee applies on/after this date" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Late Registration Time" type="time" value={lateTime}
                    onChange={e => setLateTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small" />
                </Grid>
                {/* Optional event-day times. Display-only fields so admins and
                    players know when the tournament actually runs (independent
                    of registration / refund windows). Blank = not set. */}
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Event Start Time (optional)" type="time" value={eventStartTime}
                    onChange={e => setEventStartTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small"
                    helperText="When the tournament actually starts" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Event End Time (optional)" type="time" value={eventEndTime}
                    onChange={e => setEventEndTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small"
                    helperText="When the tournament is expected to end" />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Event Arrival Time (optional)" type="time" value={eventArrivalTime}
                    onChange={e => setEventArrivalTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} size="small"
                    helperText="When participants should arrive (typically before start)" />
                </Grid>
              </Grid> : null}

              {/* ── TAB 2: Divisions ── */}
              {activeTab === 2 ? <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Divisions *</Typography>
                {groupedEntries.map(([groupName, divs]) => (
                  <Box key={groupName} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Chip label={groupName} color={GROUP_COLORS[groupName] || 'default'} size="small" />
                      <Box sx={{ flexGrow: 1, height: 1, bgcolor: 'divider' }} />
                    </Box>
                    <Grid container spacing={1.5}>
                      {divs.map(div => {
                        const waveForDiv = getWaveForDiv(div.name);
                        return (
                          <Grid size={{ xs: 6, sm: 4 }} key={div.id}>
                            <Box sx={{
                              border: '1px solid', borderRadius: 2, p: 1.5,
                              borderColor: selectedDivs[div.name] ? 'primary.main' : 'divider',
                              bgcolor: selectedDivs[div.name] ? 'primary.50' : 'transparent',
                              cursor: 'pointer',
                            }} onClick={() => toggleDiv(div.name)}>
                              <FormControlLabel
                                control={
                                  <Checkbox size="small" checked={!!selectedDivs[div.name]}
                                            onChange={e => { e.stopPropagation(); toggleDiv(div.name); }}
                                            onClick={e => e.stopPropagation()} />
                                }
                                label={<Typography variant="body2" fontWeight="bold">{div.name}</Typography>}
                                sx={{ m: 0 }}
                              />
                              {/* Wave badge */}
                              {waveForDiv && selectedDivs[div.name] && (
                                <Chip label={`${waveForDiv.name}`} size="small" color="primary"
                                      variant="outlined" icon={<WavesIcon />}
                                      sx={{ fontSize: 10, height: 20, mb: 0.5 }} />
                              )}
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                                <TextField size="small" label="Cap" type="number" inputProps={{ min:1 }}
                                           placeholder={(() => {
                                             if (div.cap != null) return String(div.cap);
                                             if (groupDefaults.caps[div.group_name] != null) return String(groupDefaults.caps[div.group_name]);
                                             return '∞';
                                           })()}
                                           value={caps[div.name] || ''}
                                           onChange={e => setCaps(c => ({ ...c, [div.name]: e.target.value }))}
                                           onClick={e => e.stopPropagation()} sx={{ width: 70 }} />
                                <TextField size="small" label="$" type="number"
                                           inputProps={{ min:0, step:'0.01' }}
                                           placeholder={(() => {
                                             if (div.price != null && Number(div.price) > 0) return String(Number(div.price).toFixed(0));
                                             if (groupDefaults.entry_fees[div.group_name] != null) return String(Number(groupDefaults.entry_fees[div.group_name]).toFixed(0));
                                             return '0';
                                           })()}
                                           value={priceOverrides[div.name] || ''}
                                           onChange={e => setPriceOverrides(p => ({ ...p, [div.name]: e.target.value }))}
                                           onClick={e => e.stopPropagation()} sx={{ width: 70 }} />
                                <TextField size="small" label="Late $" type="number"
                                           inputProps={{ min:0, step:'0.01' }}
                                           placeholder={(() => {
                                             if (div.default_late_fee != null) return String(Number(div.default_late_fee).toFixed(0));
                                             if (groupDefaults.late_fees[div.group_name] != null) return String(Number(groupDefaults.late_fees[div.group_name]).toFixed(0));
                                             return '0';
                                           })()}
                                           value={lateFeeOverrides[div.name] || ''}
                                           onChange={e => setLateFeeOverrides(p => ({ ...p, [div.name]: e.target.value }))}
                                           onClick={e => e.stopPropagation()} sx={{ width: 80 }}
                                           title="Flat fee added when registration is after the late date/time. Blank inherits division's default, then group's default." />
                                <TextField size="small" label="Court" select
                                           value={divCourts[div.name] || ''}
                                           onChange={e => setDivCourts(c => ({ ...c, [div.name]: e.target.value }))}
                                           onClick={e => e.stopPropagation()} sx={{ width: 80 }}>
                                  <MenuItem value="">—</MenuItem>
                                  {[1,2,3,4,5,6].map(n => <MenuItem key={n} value={String(n)}>{n}</MenuItem>)}
                                </TextField>
                              </Box>
                              {div.group_name === 'Camps & Clinics' && selectedDivs[div.name] && (
                                <FormControlLabel
                                  control={
                                    <Checkbox size="small" checked={!!freeIfRegistered[div.name]}
                                              onChange={e => { e.stopPropagation(); setFreeIfRegistered(f => ({ ...f, [div.name]: e.target.checked })); }}
                                              onClick={e => e.stopPropagation()} />
                                  }
                                  label={<Typography variant="caption" color="info.main" fontWeight="bold">Free if already registered</Typography>}
                                  sx={{ m: 0, mt: 0.5, display: 'flex' }}
                                  onClick={e => e.stopPropagation()}
                                />
                              )}
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                ))}
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={<Switch checked={saveAsDefault} onChange={e => setSaveAsDefault(e.target.checked)} />}
                    label="Save division selection as default for future tournaments" />
                </Box>
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    control={<Switch checked={published} onChange={e => setPublished(e.target.checked)} />}
                    label={isEdit ? 'Published (visible to the public)' : 'Publish immediately'} />
                </Box>
              </Box> : null}

              {/* ── TAB 3: Wave Schedule ── */}
              {activeTab === 3 ? <Box>
              {/* Wave Schedule — always expanded */}
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <WavesIcon color="primary" fontSize="small" />
                <Typography fontWeight="bold">Wave Schedule</Typography>
                <Chip label={`${waves.length} wave${waves.length !== 1 ? 's' : ''}`} size="small" color="primary" />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {waves.map((wave, wIdx) => (
                        <Card key={wIdx} variant="outlined" sx={{ borderRadius: 2 }}>
                          <Box sx={{ p: 1.5, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
							<Typography variant="body1" fontWeight="bold" sx={{ minWidth: 70 }}>Wave {wIdx + 1}</Typography>
                            <TextField size="small" label="Day" select value={wave.day}
                                       onChange={e => updateWave(wIdx, 'day', Number(e.target.value))}
                                       sx={{ width: 80 }} SelectProps={{ native: true }}>
                              <option value={1}>Day 1</option>
                              <option value={2}>Day 2</option>
                              <option value={3}>Day 3</option>
                            </TextField>
                            <TextField size="small" label="Start" type="time" value={wave.start_time}
                                       onChange={e => updateWave(wIdx, 'start_time', e.target.value)}
                                       InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                            <TextField size="small" label="End" type="time" value={wave.end_time}
                                       onChange={e => updateWave(wIdx, 'end_time', e.target.value)}
                                       InputLabelProps={{ shrink: true }} sx={{ width: 130 }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
                              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {fmtTime(wave.start_time)} – {fmtTime(wave.end_time)}
                              </Typography>
                              {wIdx > 0 && (
                                <Tooltip title="Remove wave">
                                  <IconButton size="small" color="error" onClick={() => removeWave(wIdx)}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>

                          {/* Divisions in this wave */}
                          <Box sx={{ p: 1.5 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                              Divisions in this wave:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: 28 }}>
                              {(wave.divisions || []).filter(d => selectedDivNames.includes(d)).length === 0 ? (
                                <Typography variant="caption" color="text.disabled" fontStyle="italic">None</Typography>
                              ) : (
                                (wave.divisions || []).filter(d => selectedDivNames.includes(d)).map(divName => {
                                  const divObj = divisions.find(d => d.name === divName);
                                  return (
                                    <Chip key={divName} label={divName} size="small"
                                          color={GROUP_COLORS[divObj?.group_name] || 'default'}
                                          onDelete={wIdx > 0 ? () => removeDivFromWave(wIdx, divName) : undefined} />
                                  );
                                })
                              )}
                            </Box>

                            {/* Move divisions from other waves to this wave */}
                            {wIdx > 0 && waves.some((w, i) => i !== wIdx && (w.divisions || []).filter(d => selectedDivNames.includes(d)).length > 0) && (
                              <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                  Move from another wave:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {waves.flatMap((w, i) =>
                                    i !== wIdx
                                      ? (w.divisions || []).filter(d => selectedDivNames.includes(d)).map(divName => {
                                          const divObj = divisions.find(d => d.name === divName);
                                          return (
                                            <Chip key={`${i}-${divName}`} size="small" variant="outlined"
                                                  label={`${divName} (${w.name})`}
                                                  color={GROUP_COLORS[divObj?.group_name] || 'default'}
                                                  onClick={() => moveDivToWave(wIdx, divName)}
                                                  sx={{ cursor: 'pointer', opacity: 0.7, '&:hover': { opacity: 1 } }} />
                                          );
                                        })
                                      : []
                                  )}
                                </Box>
                              </>
                            )}
                          </Box>
                        </Card>
                      ))}
                    </Box>

                {waves.length < 6 && (
                  <Button sx={{ mt: 2 }} variant="outlined" size="small"
                          startIcon={<AddIcon />} onClick={addWave}>
                    Add Wave
                  </Button>
                )}
              </Box> : null}

              {/* ── TAB 4: Info Page ── */}
              {activeTab === 4 ? <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Tournament Info Page</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Replaces the date/location header on this tournament's page. Leave blank for default.
                </Typography>
                {/* Token picker. Selecting a token inserts {{tokenName}} at
                    the cursor position in the Quill editor. The token is
                    substituted with the live value on the public tournament
                    page (TournamentDetail) — see substituteTokens(). */}
                <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    select size="small" label="Insert token" value="" sx={{ minWidth: 220 }}
                    onChange={e => {
                      const token = e.target.value;
                      if (!token) return;
                      const q = quillInst.current;
                      if (!q) return;
                      const range = q.getSelection(true);
                      const pos = range ? range.index : q.getLength();
                      q.insertText(pos, `{{${token}}}`, 'user');
                      q.setSelection(pos + token.length + 4, 0);
                    }}
                    helperText="Inserts a placeholder that's replaced with the live value on the public page"
                  >
                    <MenuItem value="name">Tournament Name</MenuItem>
                    <MenuItem value="date">Tournament Date</MenuItem>
                    <MenuItem value="eventStartTime">Event Start Time</MenuItem>
                    <MenuItem value="eventArrivalTime">Event Arrival Time</MenuItem>
                    <MenuItem value="eventEndTime">Event End Time</MenuItem>
                    <MenuItem value="close_date">Registration Close Date</MenuItem>
                    <MenuItem value="close_time">Registration Close Time</MenuItem>
                    <MenuItem value="refund_date">Refund Cutoff Date</MenuItem>
                    <MenuItem value="refund_time">Refund Cutoff Time</MenuItem>
                    <MenuItem value="late_date">Late Registration Date</MenuItem>
                    <MenuItem value="late_time">Late Registration Time</MenuItem>
                    <MenuItem value="sanctioning_body">Sanctioning Body</MenuItem>
                  </TextField>
                </Box>
                <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/quill/1.3.7/quill.snow.min.css" />
                <div id="quill-tournament" style={{ minHeight: 160, background: 'white' }}></div>
              </Box> : null}

              {activeTab === 5 ? <Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>Event Staff</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Assign staff to this event, then log their hours. Logged hours are added to that person's pay owed and appear in their Staff record.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
                  <TextField select size="small" label="Add staff" value={staffPickId}
                    onChange={e => setStaffPickId(e.target.value)} sx={{ minWidth: 260 }}>
                    <MenuItem value="">Select a person…</MenuItem>
                    {staffPeople.filter(p => !staffRoster.some(r => r.userId === p.userId)).map(p => (
                      <MenuItem key={p.userId} value={p.userId}>{p.name}</MenuItem>
                    ))}
                  </TextField>
                  <Button variant="outlined" onClick={addStaffToRoster} disabled={!staffPickId}>Add</Button>
                </Box>

                {staffRoster.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No staff assigned yet.</Typography>
                ) : (
                  staffRoster.map(r => {
                    const personEntries = staffEntries.filter(en => en.workerId === r.userId);
                    const loggedTotal = personEntries.reduce((s, en) => s + (en.amount || 0), 0);
                    const person = staffPeople.find(sp => sp.userId === r.userId);
                    const form = staffHoursForm[r.userId] || {};
                    return (
                      <Box key={r.userId} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, p: 2, mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography fontWeight="bold">{r.name}</Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {loggedTotal > 0 && <Typography variant="body2" sx={{ color: '#0E6251', fontWeight: 700 }}>${loggedTotal.toFixed(2)} logged</Typography>}
                            <Button size="small" color="error" onClick={() => removeStaffFromRoster(r.userId)}>Remove</Button>
                          </Box>
                        </Box>
                        {isEdit ? (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField label="Hours" type="number" size="small" sx={{ width: 100 }}
                              value={form.hours || ''}
                              onChange={e => setStaffHoursForm(prev => ({ ...prev, [r.userId]: { ...prev[r.userId], hours: e.target.value } }))} />
                            <TextField label="Rate" type="number" size="small" sx={{ width: 120 }}
                              placeholder={person?.hourlyRate != null ? String(person.hourlyRate) : ''}
                              value={form.rate != null ? form.rate : ''}
                              onChange={e => setStaffHoursForm(prev => ({ ...prev, [r.userId]: { ...prev[r.userId], rate: e.target.value } }))}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                            <Button variant="contained" size="small" disabled={staffSavingId === r.userId}
                              onClick={() => logStaffHours(r)}
                              sx={{ backgroundColor: '#0E6251', '&:hover': { backgroundColor: '#0B5345' } }}>
                              {staffSavingId === r.userId ? '…' : 'Log Hours'}
                            </Button>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Save the tournament to log hours for this person.</Typography>
                        )}
                        {personEntries.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            {personEntries.map(en => (
                              <Typography key={en.id} variant="caption" sx={{ display: 'block', color: '#666' }}>
                                {en.date} · {en.hours}h × ${en.rate} = ${(en.amount || 0).toFixed(2)}{en.paid ? ' (paid)' : ''}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box> : null}

              {/* Save / Cancel — always visible */}
              <Box sx={{ display: 'flex', gap: 2, mt: 3, pt: 3, borderTop: '1px solid #eee' }}>
                <Button type="submit" variant="contained" startIcon={saving ? null : <SaveIcon />} disabled={saving}>
                  {saving ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Save Changes' : 'Save Tournament')}
                </Button>
                <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate('/tournament-admin')}>
                  Cancel
                </Button>
              </Box>
            </Box>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}

